import {
  confirmSignUp,
  fetchAuthSession,
  getCurrentUser,
  signIn,
  signOut,
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Cookies from "universal-cookie";
import Configuration from "../config";
import { hideLoading, showLoading } from "../reducers/app";
import { wipeUser } from "../reducers/auth";
import { isLocalhost } from "./helpers";
import LogEventsService from "./logEventService";

const cookies = new Cookies();

const useAmplifyAuth = () => {
  const dispatch = useDispatch();
  const authState = useSelector((state) => state.auth);
  const [triggerFetch, setTriggerFetch] = useState(false);
  const [fetching] = useState(false);
  const [doneFetching] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const handleAuthEvent = ({ payload }) => {
      const { event } = payload;

      switch (event) {
        case "signedIn":
        case "signIn":
        case "signInWithRedirect":
          console.log("Evento Auth ricevuto:", event);
          if (isMounted) {
            setTriggerFetch(true);
          }
          break;

        case "signedOut":
        case "signOut":
          if (isMounted) {
            console.log("Utente disconnesso");
          }
          break;

        default:
          break;
      }
    };

    const unsubscribe = Hub.listen("auth", handleAuthEvent);

    return () => {
      unsubscribe();
      isMounted = false;
    };
  }, [triggerFetch, fetching, doneFetching, dispatch]);

  const handleSignin = async (username, password) => {
    const logEvents = new LogEventsService();

    try {
      await dispatch(showLoading());

      const { isSignedIn, nextStep } = await signIn({ username, password });

      if (
        nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
      ) {
        return { ...nextStep, challengeName: "NEW_PASSWORD_REQUIRED" };
      }

      if (isSignedIn) {
        const expirationDate = new Date().getTime() + 60 * 60 * 1000;
        localStorage.setItem("session_expires_in", expirationDate);

        const session = await fetchAuthSession();

        const idToken = session.tokens?.idToken;
        const groups = idToken?.payload["cognito:groups"];

        if (groups && groups[0] === "backoffice-manager") {
          logEvents.loggedLogin(username);
        }

        return await getCurrentUser();
      }
    } catch (error) {
      logEvents.loggedRetryLogin(username);
      console.log("Error signing in user ", error);
      localStorage.removeItem("session_expires_in");
      if (
        error.name === "UserAlreadyAuthenticatedException" ||
        error.message.includes("already a signed in user")
      ) {
        console.log(
          "Sessione fantasma rilevata. Eseguo logout forzato e riprovo..."
        );

        try {
          await signOut();
          return handleSignin(username, password);
        } catch (retryError) {
          console.error("Errore fatale dopo retry:", retryError);
          dispatch(hideLoading());
          throw retryError;
        }
      }

      throw error;
    }
  };

  const confirmSignup = async (username, code) => {
    try {
      await dispatch(showLoading());

      const response = await confirmSignUp({
        username,
        confirmationCode: code,
      });

      if (response) {
        return response;
      }
      return;
    } catch (error) {
      console.error("Error confirm user ", error);
      throw error;
    }
  };

  const handleSignout = async (redirectURL = "") => {
    try {
      const user = authState?.user || {};
      const username = user?.username;

      console.log("handling signout");
      const oAuthLogoutRedirectUri = isLocalhost
        ? `https://127.0.0.1:3000/${Configuration.get(
            "REACT_APP_OAUTH_REDIRECT_LOGOUT"
          )}`
        : `${Configuration.get(
            "REACT_APP_GCAP_PUBLIC_HOST"
          )}/${Configuration.get("REACT_APP_OAUTH_REDIRECT_LOGOUT")}`;

      await dispatch(showLoading());
      const logEvents = new LogEventsService();

      await signOut();

      setTriggerFetch(false);
      localStorage.removeItem("session_expires_in");
      await dispatch(hideLoading());
      dispatch({ type: "RESET_USER_DATA" });
      await dispatch(wipeUser());
      cookies.remove("language", { path: "/" });
      localStorage.removeItem("privateUserDetails");
      localStorage.clear();

      /** reset chat **/
      if (
        window.embeddedservice_bootstrap &&
        window.embeddedservice_bootstrap.userVerificationAPI
      ) {
        window.embeddedservice_bootstrap.userVerificationAPI
          .clearSession(true)
          .then(() => {
            console.log("Chat session cleared");
          })
          .catch((error) => {
            console.log("Failed to clear chat session");
          })
          .finally(() => {
            dispatch(showLoading());
          });
      }

      if (user.role === "MANAGER") {
        logEvents.loggedLogout(username);
      }

      console.log("redirecting...");
      window.location = `https://${Configuration.get(
        "REACT_APP_OAUTH_URL"
      )}/logout?client_id=${Configuration.get(
        "REACT_APP_OAUTH_CLIENT_ID"
      )}&logout_uri=${oAuthLogoutRedirectUri}`;
    } catch (error) {
      console.error("Error signing out user ", error);
      throw error;
    }
  };

  const refreshToken = async () => {
    try {
      await fetchAuthSession({ forceRefresh: true });
    } catch (e) {
      console.error("Unable to refresh Token", e);
    }
  };

  return {
    state: authState,
    fetching,
    doneFetching,
    handleSignout,
    handleSignin,
    refreshToken,
    confirmSignup,
  };
};

export default useAmplifyAuth;
